import { motion } from 'framer-motion'
import { X, Circle } from 'lucide-react'
import { TicTacToeState } from '../../../types/game.types'
import { getWinningLine } from './TicTacToeLogic'

interface TicTacToeBoardProps {
  state: TicTacToeState
  onCellClick: (row: number, col: number) => void
  disabled?: boolean
}

export default function TicTacToeBoard({ state, onCellClick, disabled = false }: TicTacToeBoardProps) {
  const winningLine = getWinningLine(state.board)
  
  const isWinningCell = (row: number, col: number) => {
    return winningLine.some(([r, c]) => r === row && c === col)
  }
  
  return (
    <div className="relative">
      <div className="grid grid-cols-3 gap-4 bg-surface rounded-2xl p-6 shadow-xl">
        {state.board.map((row, rowIndex) => (
          row.map((cell, colIndex) => {
            const isWinning = isWinningCell(rowIndex, colIndex)
            const isDisabled = disabled || cell !== null || state.winner !== null || state.isDraw
            
            return (
              <motion.button
                key={`${rowIndex}-${colIndex}`}
                className={`
                  relative w-24 h-24 rounded-xl flex items-center justify-center
                  ${isWinning 
                    ? 'bg-primary/20 border-2 border-primary' 
                    : 'bg-surface border border-border'
                  }
                  ${!isDisabled 
                    ? 'hover:bg-surface/80 cursor-pointer' 
                    : 'cursor-default'
                  }
                  transition-all duration-200
                `}
                onClick={() => !isDisabled && onCellClick(rowIndex, colIndex)}
                disabled={isDisabled}
                whileHover={!isDisabled ? { scale: 1.05 } : undefined}
                whileTap={!isDisabled ? { scale: 0.95 } : undefined}
                aria-label={`Cell ${rowIndex + 1}, ${colIndex + 1}. ${cell ? `Contains ${cell}` : 'Empty'}`}
              >
                {cell === 'X' && (
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                  >
                    <X className="w-16 h-16 text-primary" strokeWidth={3} />
                  </motion.div>
                )}
                
                {cell === 'O' && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                  >
                    <Circle className="w-16 h-16 text-secondary" strokeWidth={3} />
                  </motion.div>
                )}
                
                {!cell && !isDisabled && (
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-20"
                    initial={false}
                    animate={{ opacity: 0 }}
                    whileHover={{ opacity: 0.2 }}
                  >
                    {state.currentPlayer === 'X' ? (
                      <X className="w-12 h-12 text-text-muted" />
                    ) : (
                      <Circle className="w-12 h-12 text-text-muted" />
                    )}
                  </motion.div>
                )}
                
                {/* Winning cell highlight */}
                {isWinning && (
                  <motion.div
                    className="absolute inset-0 bg-primary/10 rounded-xl"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  />
                )}
              </motion.button>
            )
          })
        ))}
      </div>
      
      {/* Grid lines */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-0 right-0 h-0.5 bg-border/50 -translate-y-1/2" />
        <div className="absolute top-2/3 left-0 right-0 h-0.5 bg-border/50 -translate-y-1/2" />
        <div className="absolute left-1/3 top-0 bottom-0 w-0.5 bg-border/50 -translate-x-1/2" />
        <div className="absolute left-2/3 top-0 bottom-0 w-0.5 bg-border/50 -translate-x-1/2" />
      </div>
    </div>
  )
}